import { useState } from 'react';
import { Alert } from '@mui/material';
import ActionDialog from '../../../components/ActionDialog';
import BookingContactUserSelect from './BookingContactUserSelect';
import { getBlockingInUseBooking } from '../constants';
import type { Booking, SubcontractorUser } from '../types';
import { BOOKING_PICKUP_LABELS as LABELS } from '../../../utils/labels';

interface BookingPickupHandinDialogProps {
  mode: 'pickup' | 'handin';
  booking: Booking;
  machineBookings: Booking[];
  subcontractorUsers: SubcontractorUser[];
  onClose: () => void;
  onConfirm: (bookingId: string, contactUserId: string) => Promise<boolean>;
  onAddSubcontractorUser: (
    subcontractorId: string,
    firstName: string,
    lastName: string,
  ) => Promise<SubcontractorUser | null>;
}

export default function BookingPickupHandinDialog({
  mode,
  booking,
  machineBookings,
  subcontractorUsers,
  onClose,
  onConfirm,
  onAddSubcontractorUser,
}: BookingPickupHandinDialogProps) {
  const defaultContactId =
    mode === 'pickup'
      ? booking.subcontractor_user_id
      : booking.pickup_subcontractor_user_id ?? booking.subcontractor_user_id;

  const [contactUserId, setContactUserId] = useState(defaultContactId);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // A pickup can't be confirmed while another booking on this machine is still physically
  // out (picked up, not yet handed in) — the machine hasn't actually come back yet.
  const blockingBooking = mode === 'pickup' ? getBlockingInUseBooking(booking, machineBookings) : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (blockingBooking) return;
    if (!contactUserId) {
      setError(LABELS.contactPersonRequired);
      return;
    }
    setSaving(true);
    const ok = await onConfirm(booking.id, contactUserId);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <ActionDialog
      title={mode === 'pickup' ? LABELS.pickupDialogTitle : LABELS.handinDialogTitle}
      formId="booking-pickup-handin-form"
      saving={saving}
      onClose={onClose}
      onSubmit={handleSubmit}
      saveLabel={mode === 'pickup' ? LABELS.actionConfirmPickup : LABELS.actionConfirmHandin}
      cancelLabel={LABELS.cancelButton}
      testId={mode === 'pickup' ? 'booking-pickup-dialog' : 'booking-handin-dialog'}
    >
      <div className="flex flex-col gap-4">
        {blockingBooking && (
          <Alert severity="warning" data-testid="pickup-blocked-warning">
            {LABELS.blockedByBooking(blockingBooking.subcontractor?.name ?? '')}
          </Alert>
        )}
        <p className="text-sm text-text-secondary mb-0 mt-0">
          {mode === 'pickup' ? LABELS.pickupDialogBody : LABELS.handinDialogBody}
        </p>
        <BookingContactUserSelect
          label={LABELS.contactPersonLabel}
          addUserOptionLabel={LABELS.addUserOption}
          noUsersLabel={LABELS.noUsers}
          subcontractorId={booking.subcontractor_id}
          users={subcontractorUsers}
          value={contactUserId}
          onChange={(id) => {
            setContactUserId(id);
            if (error) setError('');
          }}
          onAddUser={onAddSubcontractorUser}
          error={error}
        />
      </div>
    </ActionDialog>
  );
}
